import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import QRCode from "qrcode";
import { useLocation } from "wouter";
import { api, ApiError, type Next } from "../api";
import { Field, icons, InlineAlert, useToast } from "../company-ui";
import { landingFor, useRefreshSession } from "./session";

/**
 * Sign-in (ALN-23, ALN-128; docs/pages/sign-in-methods.md B.1, docs/help/sign-in.md): the username,
 * then a password or a PIN, then whatever the server asks next (choose your own PIN or password,
 * set up two-step and save the backup codes, or enter a code). Every step is the same card
 * (ALN-118), built to match Avero Guest's login: the "Avero Vintner" wordmark, an icon and title, a
 * muted description, stacked fields at touch size, a full-width primary button and quiet links
 * under it. auth.css has the measurements.
 */
export function AuthLayout({ title, icon, description, children }: { title: string; icon: ReactNode; description?: ReactNode; children?: ReactNode }) {
  const h1 = useRef<HTMLHeadingElement>(null);
  // a step with nothing to type (locked, paused) would leave focus on the page: put it on the title,
  // so the new step is announced and Tab starts from there (TC-AUTH-063)
  useEffect(() => {
    const at = document.activeElement;
    if (!at || at === document.body) h1.current?.focus();
  }, [title]);
  return (
    <main className="auth">
      <div className="auth-card">
        <div className="auth-head">
          <p className="brandmark auth-brand">{icons.glass}<b>Avero Vintner</b></p>
          <h1 className="auth-title" ref={h1} tabIndex={-1}><span className="auth-title__icon">{icon}</span>{title}</h1>
          {description && <div className="auth-desc">{description}</div>}
        </div>
        <div className="auth-body">{children}</div>
      </div>
    </main>
  );
}

function ErrorText({ error, id }: { error: string | null; id?: string }) {
  return error ? <p className="auth-error" role="alert" id={id}>{icons.warning}<span>{error}</span></p> : null;
}

/** The step's error, announced and tied to its field: pass `tied(error)` as the field's errorId (TC-AUTH-064). */
function useErrorId() {
  const id = useId();
  return { id, tied: (error: string | null) => (error ? id : undefined) };
}

/** The quiet links under the button (Guest's "Forgot password?"): muted, primary on hover. */
function AuthLinks({ children }: { children: ReactNode }) {
  return <div className="auth-links">{children}</div>;
}
function AuthLink({ onClick, href, children }: { onClick?: () => void; href?: string; children: ReactNode }) {
  return href ? <a className="auth-link" href={href}>{children}</a> : <button type="button" className="auth-link" onClick={onClick}>{children}</button>;
}

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Check your connection and try again.");

/** "Signing in as <username>" with Change, on every step after the first. */
function SigningInAs({ username, onChange }: { username: string; onChange: () => void }) {
  return (
    <div className="auth-as">
      <span className="auth-as__icon" aria-hidden="true">{icons.person}</span>
      <p className="auth-as__who">Signing in as <b>{username}</b></p>
      <button type="button" className="auth-change" onClick={onChange}>Change</button>
    </div>
  );
}

type Step =
  | "username" | "password" | "pin" | "forgot" | "locked" | "pin_locked" | "pin_paused"
  | "choose_pin" | "choose_password" | "two_step_setup" | "backup_codes" | "two_step" | "backup_code";

interface Options { links: boolean; sharedDevice: boolean; wineryName?: string }

/** A full load of /sign-in: nothing of the last attempt stays in memory. */
const restartSignIn = () => window.location.assign("/sign-in");

/**
 * Leaving a half-finished sign-in (a temporary secret or two-step) ends that pending session on the
 * server first, so the next person at a shared computer can't pick it up (ALN-118 review).
 */
async function abandon() {
  try { await api("/auth/sign-out", { body: {} }); } catch { /* already gone: nothing to end */ }
  restartSignIn();
}

export function SignInPage() {
  return <SignInFlow />;
}

/** The whole B.1 flow. `initial` starts it part-way, after an emailed link (ALN-75). */
export function SignInFlow({ initial = "username", initialUsername = "" }: { initial?: Step; initialUsername?: string }) {
  const [, navigate] = useLocation();
  const refresh = useRefreshSession();
  const toast = useToast();
  const [step, setStep] = useState<Step>(initial);
  const [username, setUsername] = useState(initialUsername);
  const [options, setOptions] = useState<Options | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  // read once: the notice after an idle sign-out, then the address goes back to /sign-in
  const [idle] = useState(() => {
    const n = Number(new URLSearchParams(window.location.search).get("idle"));
    return Number.isInteger(n) && n > 0 && n <= 60 ? n : null;
  });
  useEffect(() => {
    if (window.location.search) history.replaceState(null, "", window.location.pathname);
    api<Options>("/auth/sign-in-options").then(setOptions, () => setOptions({ links: false, sharedDevice: false }));
  }, []);
  const shared = Boolean(options?.sharedDevice);

  const after = async (next: Next, extra: { backupCodesLeft?: number } = {}) => {
    if (next === "done") {
      // a person who only answers surveys lands on Team estimates (estimate.md A)
      const session = await refresh();
      if (extra.backupCodesLeft !== undefined) {
        const n = extra.backupCodesLeft;
        toast(`You have ${n} backup ${n === 1 ? "code" : "codes"} left.${n <= 2 ? " Make new ones in Your account." : ""}`);
      }
      navigate(landingFor(session));
      return;
    }
    setStep(next);
  };
  const toStart = () => { setUsername(""); setStep("username"); };

  switch (step) {
    case "username":
      return <UsernameStep shared={shared} wineryName={options?.wineryName} idle={idle} onForgot={() => setStep("forgot")}
        onNext={(name, s) => { setUsername(name); setStep(s); }} />;
    case "password":
    case "pin":
      return <SecretStep kind={step} username={username} shared={shared} onChange={toStart} onForgot={() => setStep("forgot")} onDone={after}
        onLocked={() => setStep(step === "pin" ? "pin_locked" : "locked")} onPaused={() => setStep("pin_paused")} />;
    case "forgot":
      return (
        <AuthLayout title="Forgot your password or PIN?" icon={icons.key}
          description="Ask an owner or controller at your winery. They can give you a temporary one to sign in with, and then you choose a new one.">
          <button type="button" className="btn btn--primary btn--touch auth-submit" onClick={toStart} autoFocus>Back to sign in</button>
        </AuthLayout>
      );
    case "locked":
      return (
        <AuthLayout title="Sign-in paused for 15 minutes" icon={icons.lock} description={`Too many tries for ${username}. Wait 15 minutes, then try again.`}>
          <div className="auth-form">
            <InlineAlert variant="info" title="Forgot your password? Ask an owner or controller to reset it. They can also unlock it sooner." />
            <button type="button" className="btn btn--primary btn--touch auth-submit" onClick={() => setStep("forgot")}>Forgot password?</button>
          </div>
          <AuthLinks><AuthLink onClick={toStart}>Back to sign in</AuthLink></AuthLinks>
        </AuthLayout>
      );
    case "pin_locked":
      return (
        <AuthLayout title="Locked for 15 minutes" icon={icons.lock} description={`Too many wrong PINs for ${username}. Try again after 15 minutes.`}>
          <InlineAlert variant="info" title="Ask an owner or controller to unlock it. They can unlock it right away, or reset your PIN with you." />
          <AuthLinks><AuthLink onClick={toStart}>Back to sign in</AuthLink></AuthLinks>
        </AuthLayout>
      );
    case "pin_paused":
      return (
        <AuthLayout title="PIN sign-in paused" icon={icons.lock}
          description="PIN sign-in is paused for your account after too many wrong tries. Ask your owner or controller to unlock it.">
          <button type="button" className="btn btn--primary btn--touch auth-submit" onClick={toStart} autoFocus>Back to sign in</button>
        </AuthLayout>
      );
    case "choose_pin":
      return <ChoosePin username={username} onDone={after} />;
    case "choose_password":
      return <ChoosePassword username={username} onDone={after} />;
    case "two_step_setup":
      return <TwoStepSetup onCodes={c => { setCodes(c); setStep("backup_codes"); }} />;
    case "backup_codes":
      return <BackupCodes codes={codes} shared={shared} onDone={after} />;
    case "two_step":
      return <TwoStepCode username={username} onDone={after} onBackup={() => setStep("backup_code")} />;
    case "backup_code":
      return <BackupCodeStep username={username} onDone={after} onApp={() => setStep("two_step")} onForgot={() => setStep("forgot")} />;
  }
}

/* ------------------------------------------------------------ step 1 */

function UsernameStep({ shared, wineryName, idle, onNext, onForgot }: { shared: boolean; wineryName?: string; idle: number | null; onNext: (username: string, step: "password" | "pin") => void; onForgot: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const err = useErrorId();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const name = String(new FormData(e.currentTarget).get("username") ?? "").trim();
    if (!name) { setError("Enter your username."); return; }
    setBusy(true); setError(null);
    try {
      const r = await api<{ step: "password" | "pin" }>("/auth/sign-in/start", { body: { username: name } });
      onNext(name, r.step);
    } catch (err) { setError(message(err)); setBusy(false); }
  };
  return (
    <AuthLayout title={shared && wineryName ? `Sign in to ${wineryName}` : "Sign in"} icon={icons.logIn} description="Start with your username.">
      <form className="auth-form" onSubmit={submit} noValidate>
        {idle && <InlineAlert variant="info" title={`The last person was signed out after ${idle} ${idle === 1 ? "minute" : "minutes"} without use.`} />}
        <Field label="Username" className="auth-field" errorId={err.tied(error)} hint="Your email works too, if you have one.">
          {/* a shared computer never fills or saves a username (D Security) */}
          <input name="username" className="input input--touch" type="text" autoComplete={shared ? "off" : "username"} autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={320} autoFocus />
        </Field>
        <ErrorText error={error} id={err.id} />
        <button type="submit" className="btn btn--primary btn--touch auth-submit auth-submit--lg" disabled={busy}>{busy ? "Checking…" : "Continue"}</button>
      </form>
      <AuthLinks><AuthLink onClick={onForgot}>Forgot your password or PIN?</AuthLink></AuthLinks>
    </AuthLayout>
  );
}

/* ------------------------------------------------------------ step 2 */

function SecretStep({ kind, username, shared, onChange, onForgot, onDone, onLocked, onPaused }: {
  kind: "password" | "pin"; username: string; shared: boolean; onChange: () => void; onForgot: () => void;
  onDone: (n: Next) => void; onLocked: () => void; onPaused: () => void;
}) {
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const err = useErrorId();
  const [busy, setBusy] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  const pin = kind === "pin";
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!value) { setError(pin ? "Enter the numbers of your PIN." : "Enter your password."); field.current?.focus(); return; }
    if (pin && !/^\d{4,6}$/.test(value)) { setError("Enter the numbers of your PIN."); setValue(""); field.current?.focus(); return; }
    setBusy(true); setError(null);
    try {
      const r = await api<{ next: Next }>("/auth/sign-in", { body: pin ? { username, pin: value } : { username, password: value } });
      onDone(r.next);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.code === "locked") { onLocked(); return; }
      if (err instanceof ApiError && err.code === "pin_locked") { onPaused(); return; }
      setError(message(err));
      setValue("");
      field.current?.focus();
    }
  };
  return (
    <AuthLayout title={pin ? "Enter your PIN" : "Enter your password"} icon={pin ? icons.key : icons.lock}
      description={<SigningInAs username={username} onChange={onChange} />}>
      <form className="auth-form" onSubmit={submit} noValidate>
        <Field label={pin ? "PIN (4 to 6 numbers)" : "Password"} className="auth-field" errorId={err.tied(error)}>
          {pin ? (
            <input ref={field} name="pin" className="input input--touch auth-pin" type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="off"
              value={value} onChange={e => setValue(e.target.value.replace(/\D/g, ""))} autoFocus />
          ) : (
            <input ref={field} name="password" className="input input--touch" type={show ? "text" : "password"} autoComplete={shared ? "off" : "current-password"}
              value={value} onChange={e => setValue(e.target.value)} autoFocus />
          )}
        </Field>
        {!pin && (
          <div className="auth-row">
            <button type="button" className="auth-link auth-link--sm" aria-pressed={show} onClick={() => setShow(!show)}>{show ? "Hide password" : "Show password"}</button>
            <button type="button" className="auth-link auth-link--sm" onClick={onForgot}>Forgot password?</button>
          </div>
        )}
        <ErrorText error={error} id={err.id} />
        <button type="submit" className="btn btn--primary btn--touch auth-submit auth-submit--lg" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
      </form>
      {pin && <AuthLinks><AuthLink onClick={onForgot}>Forgot your PIN?</AuthLink></AuthLinks>}
    </AuthLayout>
  );
}

/* ------------------------------------------------------------ temporary secrets */

function ChoosePin({ username, onDone }: { username: string; onDone: (n: Next) => void }) {
  const [error, setError] = useState<string | null>(null);
  const err = useErrorId();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true); setError(null);
    try {
      const r = await api<{ next: Next }>("/auth/pin/choose", { body: { pin: f.get("pin"), confirm: f.get("confirm") } });
      onDone(r.next);
    } catch (err) {
      setError(message(err)); setBusy(false);
      if (err instanceof ApiError && err.status === 401) setTimeout(restartSignIn, 2500);
    }
  };
  return (
    <AuthLayout title="Choose your PIN" icon={icons.key}
      description={<><SigningInAs username={username} onChange={() => void abandon()} /><p className="m0">Pick 4 to 6 numbers you'll remember. You'll enter them each time you sign in. Don't use one that's easy to guess.</p></>}>
      <form className="auth-form" onSubmit={submit} noValidate>
        <Field label="PIN" className="auth-field" errorId={err.tied(error)}>
          <input name="pin" className="input input--touch auth-pin" type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="off" autoFocus />
        </Field>
        <Field label="Type it again" className="auth-field">
          <input name="confirm" className="input input--touch auth-pin" type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="off" />
        </Field>
        <ErrorText error={error} id={err.id} />
        <button type="submit" className="btn btn--primary btn--touch auth-submit auth-submit--lg" disabled={busy}>{busy ? "Saving…" : "Save and sign in"}</button>
      </form>
    </AuthLayout>
  );
}

function ChoosePassword({ username, onDone }: { username: string; onDone: (n: Next) => void }) {
  const [error, setError] = useState<string | null>(null);
  const err = useErrorId();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true); setError(null);
    try {
      const r = await api<{ next: Next }>("/auth/password/choose", { body: { password: f.get("password"), confirm: f.get("confirm") } });
      onDone(r.next);
    } catch (err) {
      setError(message(err)); setBusy(false);
      if (err instanceof ApiError && err.status === 401) setTimeout(restartSignIn, 2500);
    }
  };
  return (
    <AuthLayout title="Choose your password" icon={icons.lock} description={<SigningInAs username={username} onChange={() => void abandon()} />}>
      <form className="auth-form" onSubmit={submit} noValidate>
        <Field label="New password" className="auth-field" errorId={err.tied(error)} hint="At least 12 characters. A few words you'll remember work well.">
          <input name="password" className="input input--touch" type="password" autoComplete="new-password" autoFocus />
        </Field>
        <Field label="Type it again" className="auth-field">
          <input name="confirm" className="input input--touch" type="password" autoComplete="new-password" />
        </Field>
        <ErrorText error={error} id={err.id} />
        <button type="submit" className="btn btn--primary btn--touch auth-submit auth-submit--lg" disabled={busy}>{busy ? "Saving…" : "Save and sign in"}</button>
      </form>
    </AuthLayout>
  );
}

/* ------------------------------------------------------------ two-step */

/** Field passes id, aria-describedby and aria-invalid; they go on the input, so its label names it. */
function CodeField({ name = "code", autoFocus = true, ...aria }: { name?: string; autoFocus?: boolean; id?: string; "aria-describedby"?: string; "aria-invalid"?: boolean }) {
  return <input {...aria} name={name} className="input input--touch auth-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]*" maxLength={7} autoFocus={autoFocus} />;
}

function TwoStepSetup({ onCodes }: { onCodes: (codes: string[]) => void }) {
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const err = useErrorId();
  const [busy, setBusy] = useState(false);
  const started = useRef(false);
  useEffect(() => {
    // drawn here from the otpauth URI, so the secret never leaves the browser for an image service
    if (setup) QRCode.toDataURL(setup.otpauthUrl, { errorCorrectionLevel: "M", margin: 2, width: 200 }).then(setQr, () => setQr(null));
  }, [setup]);
  useEffect(() => {
    // one secret per enrollment: a second call would replace the one on screen
    if (started.current) return;
    started.current = true;
    api<{ secret: string; otpauthUrl: string }>("/auth/mfa/setup", { body: {} }).then(setSetup, e => setError(message(e)));
  }, []);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const code = String(new FormData(e.currentTarget).get("code") ?? "").replace(/\s/g, "");
    if (!/^\d{6}$/.test(code)) { setError("Enter the 6 digits the app shows."); return; }
    setBusy(true); setError(null);
    try {
      const r = await api<{ next: "backup_codes"; backupCodes: string[] }>("/auth/mfa/enable", { body: { code } });
      onCodes(r.backupCodes);
    } catch (err) {
      setBusy(false);
      setError(err instanceof ApiError && err.code === "invalid_code" ? "That code didn't work. The code changes every 30 seconds; type the newest one." : message(err));
      if (err instanceof ApiError && err.status === 401) setTimeout(restartSignIn, 2500);
    }
  };
  return (
    <AuthLayout title="Set up two-step sign-in" icon={icons.compliance}
      description="Your winery asks for a second step when you sign in: a 6-digit code from an app on your phone. It takes about 2 minutes.">
      {!setup && <ErrorText error={error} id={err.id} />}
      {setup && (
        <>
          <ol className="auth-steps">
            <li>Put an authenticator app on your phone, such as Google Authenticator, Microsoft Authenticator or 1Password.</li>
            <li>
              <span className="auth-qrstep auth-scan">In the app, add an account and scan this square code.</span>
              {qr && <img className="auth-qr" src={qr} width={200} height={200} alt="Square code for your authenticator app." />}
              <span className="auth-qrstep">Can't scan? Type this key in the app:</span>
              <span className="auth-key" role="group" aria-label="Setup key"><code>{setup.secret.match(/.{1,4}/g)?.join(" ")}</code></span>
              {/* only phones have an app for otpauth: links; there it replaces the QR, which the same phone can't scan (auth.css) */}
              <span className="auth-applink">Tap the button to add Avero Vintner to your app. <a className="btn btn--touch" href={setup.otpauthUrl}>Open in my authenticator app</a></span>
            </li>
            <li>Type the 6-digit code the app shows.</li>
          </ol>
          <form className="auth-form" onSubmit={submit} noValidate>
            <Field label="6-digit code" className="auth-field" errorId={err.tied(error)}><CodeField autoFocus={false} /></Field>
            <ErrorText error={error} id={err.id} />
            <button type="submit" className="btn btn--primary btn--touch auth-submit auth-submit--lg" disabled={busy}>{busy ? "Checking…" : "Continue"}</button>
          </form>
        </>
      )}
      <AuthLinks><AuthLink onClick={() => void abandon()}>Back to sign in</AuthLink></AuthLinks>
    </AuthLayout>
  );
}

/** While Print prints, the printout is the codes alone (auth.css); every other printout is the page as shown. */
function printCodes() {
  const done = () => { document.body.classList.remove("printing-codes"); window.removeEventListener("afterprint", done); };
  window.addEventListener("afterprint", done);
  document.body.classList.add("printing-codes");
  window.print();
}

/** The ten codes, Print, Download (not on a shared computer, H8) and the required tick. Also used on Your account. */
export function BackupCodeList({ codes, shared, onSaved, extra, busy }: { codes: string[]; shared: boolean; onSaved: () => void | Promise<void>; extra?: ReactNode; busy?: boolean }) {
  const [ticked, setTicked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const download = () => {
    const text = `Avero Vintner backup codes\nEach code works once.\n\n${codes.map((c, i) => `${i + 1}. ${c}`).join("\n")}\n`;
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url; a.download = "avero-vintner-backup-codes.txt";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <form className="auth-form" noValidate onSubmit={e => { e.preventDefault(); if (!ticked) { setError("Tick the box once you've saved your codes. They won't be shown again."); return; } setError(null); void onSaved(); }}>
      {extra}
      <ol className="backup-codes" aria-label="Backup codes">{codes.map(c => <li key={c}>{c}</li>)}</ol>
      <div className="auth-row auth-row--even">
        <button type="button" className="btn btn--touch" onClick={printCodes}>Print</button>
        {!shared && <button type="button" className="btn btn--touch" onClick={download}>Download</button>}
      </div>
      <label className="auth-check"><input type="checkbox" checked={ticked} onChange={e => setTicked(e.target.checked)} /><span>I've saved my backup codes</span></label>
      <ErrorText error={error} />
      <button type="submit" className="btn btn--primary btn--touch auth-submit auth-submit--lg" disabled={busy}>Continue</button>
    </form>
  );
}

function BackupCodes({ codes, shared, onDone }: { codes: string[]; shared: boolean; onDone: (n: Next) => void }) {
  const [error, setError] = useState<string | null>(null);
  const err = useErrorId();
  const [busy, setBusy] = useState(false);
  const saved = async () => {
    setBusy(true); setError(null);
    try { const r = await api<{ next: Next }>("/auth/mfa/backup-codes/saved", { body: {} }); onDone(r.next); }
    catch (err) { setError(message(err)); setBusy(false); if (err instanceof ApiError && err.status === 401) setTimeout(restartSignIn, 2500); }
  };
  return (
    <AuthLayout title="Save your backup codes" icon={icons.key}
      description="If you don't have your phone, you can sign in with one of these instead of a code. Each one works once. Keep them somewhere safe, away from this computer.">
      <BackupCodeList codes={codes} shared={shared} onSaved={saved} busy={busy} />
      <ErrorText error={error} id={err.id} />
    </AuthLayout>
  );
}

function TwoStepCode({ username, onDone, onBackup }: { username: string; onDone: (n: Next, extra?: { backupCodesLeft?: number }) => void; onBackup: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const err = useErrorId();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const code = String(new FormData(form).get("code") ?? "").replace(/\s/g, "");
    if (!/^\d{6}$/.test(code)) { setError("Enter the 6 digits the app shows."); return; }
    setBusy(true); setError(null);
    try { const r = await api<{ next: Next }>("/auth/mfa/verify", { body: { code } }); onDone(r.next); }
    catch (err) {
      setBusy(false);
      setError(err instanceof ApiError && err.code === "invalid_code" ? "That code didn't work. Type the newest code from your app." : message(err));
      form.reset();
      if (err instanceof ApiError && (err.status === 401 || err.code === "locked")) setTimeout(restartSignIn, 2500);
    }
  };
  return (
    <AuthLayout title="Enter your code" icon={icons.compliance}
      description={<><p className="m0">Open your authenticator app and type the 6-digit code for Avero Vintner.</p><SigningInAs username={username} onChange={() => void abandon()} /></>}>
      <form className="auth-form" onSubmit={submit} noValidate>
        <Field label="6-digit code" className="auth-field" hint="The code changes every 30 seconds." errorId={err.tied(error)}><CodeField /></Field>
        <ErrorText error={error} id={err.id} />
        <button type="submit" className="btn btn--primary btn--touch auth-submit auth-submit--lg" disabled={busy}>{busy ? "Checking…" : "Sign in"}</button>
      </form>
      <AuthLinks><AuthLink onClick={onBackup}>Use a backup code</AuthLink><AuthLink onClick={() => void abandon()}>Back to sign in</AuthLink></AuthLinks>
    </AuthLayout>
  );
}

function BackupCodeStep({ username, onDone, onApp, onForgot }: { username: string; onDone: (n: Next, extra?: { backupCodesLeft?: number }) => void; onApp: () => void; onForgot: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const err = useErrorId();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const backupCode = String(new FormData(e.currentTarget).get("backupCode") ?? "").trim();
    if (!backupCode) { setError("Type one of your backup codes."); return; }
    setBusy(true); setError(null);
    try {
      const r = await api<{ next: Next; backupCodesLeft?: number }>("/auth/mfa/verify", { body: { backupCode } });
      onDone(r.next, { backupCodesLeft: r.backupCodesLeft });
    } catch (err) {
      setBusy(false);
      setError(err instanceof ApiError && err.code === "invalid_backup_code" ? "That backup code didn't work. Check it, or use one you haven't used before." : message(err));
      if (err instanceof ApiError && (err.status === 401 || err.code === "locked")) setTimeout(restartSignIn, 2500);
    }
  };
  return (
    <AuthLayout title="Use a backup code" icon={icons.key}
      description={<><p className="m0">Type one of the backup codes you saved when you set up two-step sign-in. Each code works once.</p><SigningInAs username={username} onChange={() => void abandon()} /></>}>
      <form className="auth-form" onSubmit={submit} noValidate>
        <Field label="Backup code" className="auth-field" errorId={err.tied(error)} hint="Dashes, spaces and capitals don't matter.">
          <input name="backupCode" className="input input--touch auth-code auth-code--bk" type="text" autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={13} placeholder="ABCDE-FGHJK" autoFocus />
        </Field>
        <ErrorText error={error} id={err.id} />
        <button type="submit" className="btn btn--primary btn--touch auth-submit auth-submit--lg" disabled={busy}>{busy ? "Checking…" : "Sign in"}</button>
      </form>
      <AuthLinks><AuthLink onClick={onApp}>Use my authenticator app instead</AuthLink><AuthLink onClick={onForgot}>No backup codes?</AuthLink></AuthLinks>
    </AuthLayout>
  );
}

/** /sign-in/link#token=…: ALN-75's emailed link (off by default). The token stays in the fragment, so it never reaches a server log. */
export function MagicLinkPage() {
  const [, navigate] = useLocation();
  const refresh = useRefreshSession();
  const [state, setState] = useState<{ step: "checking" | "failed" | Next; error?: string }>({ step: "checking" });
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = new URLSearchParams(window.location.hash.slice(1)).get("token");
    history.replaceState(null, "", window.location.pathname);
    if (!token) { setState({ step: "failed", error: "This sign-in link is incomplete. Ask for a new one." }); return; }
    api<{ next: Next }>("/auth/magic-link/verify", { body: { token } }).then(
      async r => { if (r.next === "done") { navigate(landingFor(await refresh())); } else setState({ step: r.next }); },
      e => setState({ step: "failed", error: message(e) }),
    );
  }, [navigate, refresh]);
  if (state.step !== "checking" && state.step !== "failed" && state.step !== "done") return <SignInFlow initial={state.step} />;
  return (
    <AuthLayout title={state.step === "failed" ? "That link didn't work" : "Signing you in…"} icon={state.step === "failed" ? icons.clock : icons.logIn}
      description={state.step === "failed" ? undefined : "Checking your sign-in link."}>
      <ErrorText error={state.error ?? null} />
      {state.step === "failed" && <AuthLinks><AuthLink href="/sign-in">Back to sign in</AuthLink></AuthLinks>}
    </AuthLayout>
  );
}
